import React, { useState, useMemo, useEffect, useContext } from 'react';
import { RestHelper } from '../../helper/restHelper';
import { UserContext } from '../../app';
import AdminFinancialsPaymentModal from './admin-financials-payment-modal';
import AdminFinancialsVoidModal from './admin-financials-void-modal';
import AdminFinancialsEditFeeModal from './admin-financials-edit-fee-modal';
import AdminFinancialsQBMatchModal from './admin-financials-qb-match-modal';
import AdminFinancialsDepositModal from './admin-financials-deposit-modal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const toEpoch = (dateStr, endOfDay = false) => {
  const d = new Date(dateStr);
  if (endOfDay) d.setHours(23, 59, 59, 999);
  return Math.floor(d.getTime() / 1000);
};

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

// Truncate long text to `max` chars, appending an ellipsis when clipped.
const truncate = (str, max) => {
  const s = str || '';
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

// Format a YYYY-MM-DD (or ISO) date string as MM/DD/YY. Returns '-' if empty.
const fmtDate = (dateStr) => {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('T')[0].split('-');
  if (!y || !m || !d) return dateStr;
  return `${m}/${d}/${y.slice(2)}`;
};

// Rows shown per page in the records table.
const PAGE_SIZE = 15;

// An order is "fully settled" only when BOTH sides are explicitly paid.
// A blank/pending AR (e.g. notary paid but title not yet) does NOT qualify.
const isFullySettled = (o) => o.ar_status === 'paid' && o.ap_status === 'paid';

// Day after today (range upper bound for the default 5-week window)
const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().split('T')[0];
};

// 5 weeks (35 days) before tomorrow — inclusive lower bound for default range
const fiveWeeksBeforeTomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1 - 35);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().split('T')[0];
};

const statusBadge = (status, withStrike = false) => {
  const map = {
    pending:  'bg-amber-100 text-amber-700',
    invoiced: 'bg-blue-100 text-blue-700',
    paid:     'bg-green-100 text-green-700',
    voided:   'bg-red-100 text-red-500',
  };
  const cls = map[status] || 'bg-neutral-100 text-neutral-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls} ${withStrike ? 'line-through' : ''}`}>
      {status || '—'}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------
const SummaryCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-4 flex flex-col gap-1">
    <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">{label}</span>
    <span className="text-2xl font-bold text-neutral-800">{value}</span>
    {sub && <span className="text-xs text-neutral-400">{sub}</span>}
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AdminFinancials = () => {
  const api = RestHelper();
  const { userProfile } = useContext(UserContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [qbConnected] = useState(true);
  const [firms, setFirms] = useState([]);
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [dateFrom, setDateFrom] = useState(fiveWeeksBeforeTomorrowStr());
  const [dateTo, setDateTo] = useState(tomorrowStr());
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeModal, setActiveModal] = useState(null);
  const [batchError, setBatchError] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.postCreate('/get-firm-list', {}, { activeUser: userProfile.profileEmail })
      .then(res => setFirms(res.firms || []))
      .catch(err => console.log(err));
  }, []);

  const loadRecords = (from = dateFrom, to = dateTo, status = statusFilter) => {
    setLoading(true);
    setLoadError('');
    api.postCreate('/manage-financials-admin', {}, {
      action: 'getRecords',
      date_from: from ? toEpoch(from) : 0,
      date_to: to ? toEpoch(to, true) : 9999999999,
      status_filter: status || 'all',
      search: '',
    })
      .then(res => { setOrders(res.records || []); setLoading(false); })
      .catch(() => { setLoadError('Failed to load records. Please try again.'); setLoading(false); });
  };

  useEffect(() => { loadRecords(dateFrom, dateTo, statusFilter); }, [dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  // ---------------------------------------------------------------------------
  // Filtered orders
  // ---------------------------------------------------------------------------
  const filtered = useMemo(() => {
    return orders.filter(o => {
      // The "Fully Settled" filter shows ONLY genuinely-settled rows; every other
      // view hides them. This guards against the backend returning orders that are
      // settled on one side only (e.g. notary paid, title AR still blank/pending).
      if (statusFilter === 'fully_settled') {
        if (!isFullySettled(o)) return false;
      } else if (isFullySettled(o)) {
        return false;
      }
      if (selectedFirmId && parseFloat(o.firm_id) !== parseFloat(selectedFirmId)) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        if (
          !(o.title_company || '').toLowerCase().includes(q) &&
          !(o.file_number || '').toLowerCase().includes(q) &&
          !(o.notary_name || '').toLowerCase().includes(q) &&
          !(o.appointment_name || '').toLowerCase().includes(q) &&
          !(o.pinpoint_invoice_number || '').toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [orders, searchText, selectedFirmId, statusFilter]);

  // ---------------------------------------------------------------------------
  // Pagination — slice the filtered rows into pages of PAGE_SIZE
  // ---------------------------------------------------------------------------
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // Reset to the first page whenever the filtered result set changes.
  useEffect(() => { setPage(1); }, [searchText, selectedFirmId, statusFilter, dateFrom, dateTo]);

  // Keep the current page in range if the row count shrinks under us.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  // ---------------------------------------------------------------------------
  // Summary cards (computed from ALL orders, not just filtered)
  // ---------------------------------------------------------------------------
  const summary = useMemo(() => {
    const thisMonth = new Date().toISOString().slice(0, 7);
    return {
      outstandingAR: orders.filter(o => o.ar_status === 'invoiced').reduce((s, o) => s + o.ar_amount, 0),
      owedNotaries:  orders.filter(o => o.ap_status === 'pending').reduce((s, o) => s + o.ap_amount, 0),
      pendingSync:   orders.filter(o => o.ar_status === 'pending').length,
      voidedCount:   orders.filter(o => o.ar_voided_at?.startsWith(thisMonth) || o.ap_voided_at?.startsWith(thisMonth)).length,
      get netPosition() { return this.outstandingAR - this.owedNotaries; },
    };
  }, [orders]);

  // ---------------------------------------------------------------------------
  // Undeposited payments
  // ---------------------------------------------------------------------------
  const undepositedPayments = useMemo(() => {
    const eligible = orders.filter(o => o.ar_status === 'paid' && !o.ar_deposited);
    return {
      count: eligible.length,
      total: eligible.reduce((sum, o) => sum + (o.ar_amount || 0), 0),
      orders: eligible,
    };
  }, [orders]);

  // ---------------------------------------------------------------------------
  // Selection
  // ---------------------------------------------------------------------------
  const allFilteredIds = filtered.map(o => o.order_id);
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allFilteredIds));
    }
    setBatchError('');
  };

  const toggleRow = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
    setBatchError('');
  };

  const clearSelection = () => { setSelectedIds(new Set()); setBatchError(''); };

  // ---------------------------------------------------------------------------
  // Mutation helpers — each calls the API then reloads
  // ---------------------------------------------------------------------------
  const handleSyncToQB = (orderId) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'syncToQB', order_ids: [orderId] })
      .then(() => loadRecords())
      .catch(err => console.log(err));
  };

  const handleBatchSyncToQB = () => {
    const sel = filtered.filter(o => selectedIds.has(o.order_id));
    const invalid = sel.filter(o => o.ar_status !== 'pending');
    if (invalid.length > 0) {
      setBatchError('All selected orders must have AR status "pending" to batch sync.');
      return;
    }
    api.postCreate('/manage-financials-admin', {}, { action: 'syncToQB', order_ids: sel.map(o => o.order_id) })
      .then(() => { clearSelection(); loadRecords(); })
      .catch(err => console.log(err));
  };

  const handleRecordARPayment = (orderIds, checkNumber, paymentDate) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'recordArPayment', order_ids: orderIds, check_number: checkNumber, payment_date: paymentDate })
      .then(() => { setActiveModal(null); clearSelection(); loadRecords(); })
      .catch(err => console.log(err));
  };

  const handleRecordAPPayment = (orderIds, checkNumber, paymentDate) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'recordApPayment', order_ids: orderIds, check_number: checkNumber, payment_date: paymentDate })
      .then(() => { setActiveModal(null); clearSelection(); loadRecords(); })
      .catch(err => console.log(err));
  };

  const handleVoid = (orderId, side, reason) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'voidRecord', order_id: orderId, record_type: side, reason })
      .then(() => { setActiveModal(null); loadRecords(); })
      .catch(err => console.log(err));
  };

  const handleEditFee = (orderId, side, amount, reason) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'editAmount', order_id: orderId, record_type: side, amount, reason })
      .then(() => { setActiveModal(null); loadRecords(); })
      .catch(err => console.log(err));
  };

  // ---------------------------------------------------------------------------
  // QB entity initialization handlers
  // ---------------------------------------------------------------------------
  const handleInitializeCustomer = (order) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'initializeCustomer', firm_id: order.firm_id })
      .then(res => {
        if (res.duplicate) {
          setActiveModal({ type: 'qb_match', entityType: 'customer', matches: res.matches, firm_id: order.firm_id });
        } else {
          loadRecords();
        }
      })
      .catch(err => console.log(err));
  };

  const handleInitializeVendor = (order) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'initializeVendor', notary_email: order.notary_email })
      .then(res => {
        if (res.duplicate) {
          setActiveModal({ type: 'qb_match', entityType: 'vendor', matches: res.matches, notary_email: order.notary_email });
        } else {
          loadRecords();
        }
      })
      .catch(err => console.log(err));
  };

  const handleLinkEntity = (entityType, qbId) => {
    const action = entityType === 'customer' ? 'linkCustomer' : 'linkVendor';
    const payload = entityType === 'customer'
      ? { firm_id: activeModal.firm_id, qb_id: qbId }
      : { notary_email: activeModal.notary_email, qb_id: qbId };
    api.postCreate('/manage-financials-admin', {}, { action, ...payload })
      .then(() => { setActiveModal(null); loadRecords(); })
      .catch(err => console.log(err));
  };

  const handleForceCreate = (entityType) => {
    const action = entityType === 'customer' ? 'forceCreateCustomer' : 'forceCreateVendor';
    const payload = entityType === 'customer'
      ? { firm_id: activeModal.firm_id }
      : { notary_email: activeModal.notary_email };
    api.postCreate('/manage-financials-admin', {}, { action, ...payload })
      .then(() => { setActiveModal(null); loadRecords(); })
      .catch(err => console.log(err));
  };

  const handleCreateDeposit = (orderIds, depositDate) => {
    api.postCreate('/manage-financials-admin', {}, { action: 'createDeposit', order_ids: orderIds, deposit_date: depositDate })
      .then(() => { setActiveModal(null); loadRecords(); })
      .catch(err => console.log(err));
  };

  // ---------------------------------------------------------------------------
  // Batch action handlers
  // ---------------------------------------------------------------------------
  const openBatchARPayment = () => {
    const sel = filtered.filter(o => selectedIds.has(o.order_id));
    const firmIds = new Set(sel.map(o => o.firm_id));
    if (firmIds.size > 1) {
      setBatchError('Batch title company payment requires all selected orders to be from the same firm.');
      return;
    }
    const invalid = sel.filter(o => o.ar_status !== 'invoiced');
    if (invalid.length > 0) {
      setBatchError('All selected orders must have AR status "invoiced" to record a batch payment.');
      return;
    }
    setActiveModal({ type: 'payment_ar', orderIds: sel.map(o => o.order_id) });
    setBatchError('');
  };

  const openBatchAPPayment = () => {
    const sel = filtered.filter(o => selectedIds.has(o.order_id));
    const notaryIds = new Set(sel.map(o => o.notary_id));
    if (notaryIds.size > 1) {
      setBatchError('Batch notary payment requires all selected orders to be for the same notary.');
      return;
    }
    const invalid = sel.filter(o => o.ap_status !== 'pending');
    if (invalid.length > 0) {
      setBatchError('All selected orders must have AP status "pending" to record a batch notary payment.');
      return;
    }
    setActiveModal({ type: 'payment_ap', orderIds: sel.map(o => o.order_id) });
    setBatchError('');
  };

  const selectedCount = selectedIds.size;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* QB reconnect banner */}
      {!qbConnected && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-xl px-4 py-3 flex items-center gap-3 text-sm">
          <span>QuickBooks connection lost.</span>
          <button className="underline font-medium hover:text-amber-900">Reconnect QuickBooks</button>
        </div>
      )}

      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-neutral-800">Financials</h1>
        <div className="w-12 h-1 bg-blue-500 rounded mt-1"></div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <SummaryCard label="Outstanding AR" value={fmt(summary.outstandingAR)} sub="invoiced, unpaid" />
        <SummaryCard label="Owed to Notaries" value={fmt(summary.owedNotaries)} sub="pending payment" />
        <SummaryCard label="Pending QB Sync" value={summary.pendingSync} sub="orders not yet in QB" />
        <SummaryCard
          label="Net Position"
          value={fmt(summary.netPosition)}
          sub={summary.netPosition >= 0 ? 'net receivable' : 'net payable'}
        />
        <SummaryCard label="Voided This Month" value={summary.voidedCount} sub="records" />
      </div>

      {/* Undeposited payments banner */}
      {undepositedPayments.count > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-green-800 font-medium">
            {undepositedPayments.count} payment{undepositedPayments.count !== 1 ? 's' : ''} (${undepositedPayments.total.toFixed(2)}) ready to deposit
          </span>
          <button
            onClick={() => setActiveModal({ type: 'deposit' })}
            className="shrink-0 px-4 py-1.5 text-sm font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Create Deposit
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 px-4 sm:px-6 py-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1 uppercase tracking-wide">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1 uppercase tracking-wide">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1 uppercase tracking-wide">Status</label>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="unpaid_ar">Unpaid AR</option>
              <option value="unpaid_ap">Unpaid AP</option>
              <option value="fully_settled">Fully Settled</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1 uppercase tracking-wide">Title Company</label>
            <select
              value={selectedFirmId}
              onChange={e => setSelectedFirmId(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">All firms</option>
              {firms.map(f => (
                <option key={f.org_id} value={f.org_id}>{f.org_name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-neutral-500 mb-1 uppercase tracking-wide">Search</label>
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Title company, file #, notary..."
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              className="px-3 py-2 text-sm text-neutral-500 hover:text-neutral-700"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Batch action bar */}
      {selectedCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-blue-800">{selectedCount} selected</span>
          <button
            onClick={handleBatchSyncToQB}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Batch Sync to QB
          </button>
          <button
            onClick={openBatchARPayment}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Batch Title Co Payment
          </button>
          <button
            onClick={openBatchAPPayment}
            className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            Batch Notary Payment
          </button>
          <button
            onClick={clearSelection}
            className="ml-auto px-3 py-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-800 rounded-lg"
          >
            Clear selection
          </button>
          {batchError && (
            <p className="w-full text-xs text-red-600 mt-1">{batchError}</p>
          )}
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">{loadError}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-200 overflow-hidden">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
        {!loading && (<><div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="rounded border-neutral-300"
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">Title Company</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">Appointment</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">Appt Date</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">Notary</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">Title Fee</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-neutral-500 uppercase tracking-wide">AR</th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide whitespace-nowrap">Notary Fee</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-neutral-500 uppercase tracking-wide">AP</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-neutral-400">
                    No records match the current filters.
                  </td>
                </tr>
              )}
              {paged.map(o => {
                const isVoided = o.ar_status === 'voided' && o.ap_status === 'voided';

                return (
                  <tr
                    key={o.order_id}
                    className={`hover:bg-neutral-50 transition-colors ${isVoided ? 'opacity-50' : ''}`}
                  >
                    {/* Checkbox */}
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(o.order_id)}
                        onChange={() => toggleRow(o.order_id)}
                        className="rounded border-neutral-300"
                      />
                    </td>

                    {/* Title company */}
                    <td className="px-3 py-3 text-neutral-600 whitespace-nowrap" title={o.title_company}>{truncate(o.title_company, 25)}</td>

                    {/* Appointment */}
                    <td className="px-3 py-3 text-neutral-600 whitespace-nowrap" title={o.appointment_name}>{truncate(o.appointment_name, 15)}</td>

                    {/* Appointment date */}
                    <td className="px-3 py-3 text-neutral-600 whitespace-nowrap">{fmtDate(o.appt_date)}</td>

                    {/* Notary */}
                    <td className="px-3 py-3 text-neutral-600 whitespace-nowrap">
                      {o.notary_name}
                      {o.qb_vendor_id && o.qb_vendor_1099 === false && (
                        <span className="ml-1 text-xs text-amber-600 font-medium">· 1099 Incomplete</span>
                      )}
                    </td>

                    {/* AR amount */}
                    <td className="px-3 py-3 text-right font-medium text-neutral-700 whitespace-nowrap">{fmt(o.ar_amount)}</td>

                    {/* AR status */}
                    <td className="px-3 py-3 text-center">{statusBadge(o.ar_status, o.ar_status === 'voided')}</td>

                    {/* AP amount */}
                    <td className="px-3 py-3 text-right font-medium text-neutral-700 whitespace-nowrap">{fmt(o.ap_amount)}</td>

                    {/* AP status */}
                    <td className="px-3 py-3 text-center">{statusBadge(o.ap_status, o.ap_status === 'voided')}</td>

                    {/* Actions */}
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {/* Primary: Sync to QB (only if title company is initialized) */}
                        {o.ar_status === 'pending' && o.qb_customer_id && (
                          <button
                            onClick={() => handleSyncToQB(o.order_id)}
                            className="px-2 py-1 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors whitespace-nowrap"
                          >
                            Sync to QB
                          </button>
                        )}
                        {o.ar_status === 'pending' && !o.qb_customer_id && (
                          <button
                            onClick={() => handleInitializeCustomer(o)}
                            className="px-2 py-1 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded transition-colors whitespace-nowrap"
                          >
                            Setup Title Co in QB
                          </button>
                        )}

                        {/* Primary: Pay Notary (only if notary is initialized) */}
                        {o.ap_status === 'pending' && o.qb_vendor_id && (
                          <button
                            onClick={() => setActiveModal({ type: 'payment_ap', orderIds: [o.order_id] })}
                            className="px-2 py-1 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors whitespace-nowrap"
                          >
                            Pay Notary
                          </button>
                        )}
                        {o.ap_status === 'pending' && !o.qb_vendor_id && (
                          <button
                            onClick={() => handleInitializeVendor(o)}
                            className="px-2 py-1 text-xs font-medium bg-amber-500 hover:bg-amber-600 text-white rounded transition-colors whitespace-nowrap"
                          >
                            Setup Notary in QB
                          </button>
                        )}

                        {/* More menu */}
                        {(o.ar_status === 'invoiced' ||
                          !['paid','voided'].includes(o.ar_status) ||
                          !['paid','voided'].includes(o.ap_status) ||
                          o.ar_status !== 'voided' ||
                          o.ap_status !== 'voided') && (
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(openMenuId === o.order_id ? null : o.order_id);
                              }}
                              className="px-2 py-1 text-xs font-medium bg-neutral-100 hover:bg-neutral-200 text-neutral-600 rounded transition-colors"
                            >
                              More ···
                            </button>

                            {openMenuId === o.order_id && (
                              <div className="absolute right-0 z-20 mt-1 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 min-w-[180px]">
                                {o.ar_status === 'invoiced' && (
                                  <button
                                    onClick={() => { setActiveModal({ type: 'payment_ar', orderIds: [o.order_id] }); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                                  >
                                    Record Title Co Payment
                                  </button>
                                )}
                                {!['paid','voided'].includes(o.ar_status) && (
                                  <button
                                    onClick={() => { setActiveModal({ type: 'edit_fee', orderId: o.order_id, side: 'ar', currentAmount: o.ar_amount }); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                                  >
                                    Edit Title Co Fee
                                  </button>
                                )}
                                {!['paid','voided'].includes(o.ap_status) && (
                                  <button
                                    onClick={() => { setActiveModal({ type: 'edit_fee', orderId: o.order_id, side: 'ap', currentAmount: o.ap_amount }); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                                  >
                                    Edit Notary Fee
                                  </button>
                                )}
                                {(o.ar_status !== 'voided' || o.ap_status !== 'voided') && (
                                  <div className="border-t border-neutral-100 my-1" />
                                )}
                                {o.ar_status !== 'voided' && (
                                  <button
                                    onClick={() => { setActiveModal({ type: 'void', orderId: o.order_id, side: 'ar' }); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                  >
                                    Void AR
                                  </button>
                                )}
                                {o.ap_status !== 'voided' && (
                                  <button
                                    onClick={() => { setActiveModal({ type: 'void', orderId: o.order_id, side: 'ap' }); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                  >
                                    Void AP
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-between gap-3">
            <span className="text-xs text-neutral-400">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
              {filtered.length !== orders.length && ` (filtered from ${orders.length})`}
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-xs font-medium border border-neutral-200 rounded-lg text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                <span className="text-xs text-neutral-500">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-xs font-medium border border-neutral-200 rounded-lg text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        </>)}
      </div>

      {/* Modals */}
      {activeModal?.type === 'payment_ar' && (
        <AdminFinancialsPaymentModal
          type="ar"
          orderIds={activeModal.orderIds}
          orders={orders}
          onConfirm={(checkNumber, paymentDate) => handleRecordARPayment(activeModal.orderIds, checkNumber, paymentDate)}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal?.type === 'payment_ap' && (
        <AdminFinancialsPaymentModal
          type="ap"
          orderIds={activeModal.orderIds}
          orders={orders}
          onConfirm={(checkNumber, paymentDate) => handleRecordAPPayment(activeModal.orderIds, checkNumber, paymentDate)}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal?.type === 'void' && (
        <AdminFinancialsVoidModal
          orderId={activeModal.orderId}
          side={activeModal.side}
          onConfirm={(reason) => handleVoid(activeModal.orderId, activeModal.side, reason)}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal?.type === 'edit_fee' && (
        <AdminFinancialsEditFeeModal
          orderId={activeModal.orderId}
          side={activeModal.side}
          currentAmount={activeModal.currentAmount}
          onConfirm={(amount, reason) => handleEditFee(activeModal.orderId, activeModal.side, amount, reason)}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal?.type === 'qb_match' && (
        <AdminFinancialsQBMatchModal
          entityType={activeModal.entityType}
          matches={activeModal.matches}
          firmId={activeModal.firm_id}
          notaryEmail={activeModal.notary_email}
          onLink={(qbId) => handleLinkEntity(activeModal.entityType, qbId)}
          onForceCreate={() => handleForceCreate(activeModal.entityType)}
          onClose={() => setActiveModal(null)}
        />
      )}

      {activeModal?.type === 'deposit' && (
        <AdminFinancialsDepositModal
          orders={undepositedPayments.orders}
          onConfirm={handleCreateDeposit}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  );
};

export default AdminFinancials;
